import { useState, useRef, useEffect, useCallback } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Tooltip from '@radix-ui/react-tooltip'
import { Eye, Save, Loader2, AlertCircle, ChevronDown, ExternalLink } from 'lucide-react'
import { useParams, useBlocker, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useBlockCanvas } from '../editor-shared/useBlockCanvas'
import { BlockPickerShelf } from '../editor-shared/BlockPickerShelf'
import { BlockCanvas } from '../editor-shared/BlockCanvas'
import { BlockEditorDndContext } from '../editor-shared/BlockEditorDndContext'
import { LayoutRightShelf } from './LayoutRightShelf'
import { LayoutPreview } from './LayoutPreview'
import { useCreateLayout, useUpdateLayout, useLayout } from '../../api/hooks'
import type { LayoutUsageContext, PageBlockType } from '../../api/types'
import { useLayoutPreviewBroadcast } from '../../hooks/useLayoutPreviewBroadcast'

// ─── LayoutCreator ────────────────────────────────────────────────────────────────

function LayoutCreator() {
  const { id } = useParams<{ id: string }>()
  const isEditing = Boolean(id)
  const navigate = useNavigate()

  const { data: existing, isLoading: isLoadingLayout, isError: isLayoutError } = useLayout(id ?? '')
  const { mutate: createLayout, isPending: isCreating } = useCreateLayout()
  const { mutate: updateLayout, isPending: isUpdating } = useUpdateLayout(id ?? '')
  const isSaving = isCreating || isUpdating

  // Layout metadata
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [usageContext, setUsageContext] = useState<LayoutUsageContext[]>([])
  const [isEditingName, setIsEditingName] = useState(false)
  const [draftName, setDraftName] = useState('')
  const nameInputRef = useRef<HTMLInputElement>(null)
  const [isEditingDesc, setIsEditingDesc] = useState(false)
  const [draftDesc, setDraftDesc] = useState('')
  const descInputRef = useRef<HTMLInputElement>(null)
  const [showPreview, setShowPreview] = useState(false)

  const [isDirty, setIsDirtyState] = useState(false)
  const isDirtyRef = useRef(false)
  function setIsDirty(value: boolean) {
    isDirtyRef.current = value
    setIsDirtyState(value)
  }

  const canvas = useBlockCanvas(() => setIsDirty(true))

  // Broadcast live state to any open popout preview window (only when editing
  // a saved layout — new layouts have no id and can't be targeted by URL).
  useLayoutPreviewBroadcast(id ?? '', canvas.slots, name)

  function openPopoutPreview() {
    window.open(`/admin/layouts/${id}/live-preview`, 'wire-layout-live-preview', 'width=1440,height=900')
  }

  // Block browser-level unload (refresh, close tab) when dirty
  useEffect(() => {
    if (!isDirty) return
    function handleBeforeUnload(e: BeforeUnloadEvent) { e.preventDefault() }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // Block in-app navigation when dirty. The ref is read synchronously so the
  // blocker always sees the current value regardless of React batching/effect order.
  const blocker = useBlocker(useCallback(() => isDirtyRef.current, []))

  // Hydrate editor state when loading an existing layout.
  // This is intentional one-time initialization from async data — not a reactive sync.
  // TODO: refactor to key-based reset + lazy useState when this component is split.
  useEffect(() => {
    if (!existing) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setName(existing.name)
    setDescription(existing.description ?? '')
    setUsageContext(existing.usageContext)
    canvas.setSlots(
      existing.blocks
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((b) => {
          // Prefer columnBlocks from API (new format).
          // Fall back to migrating legacy scalar config[colKey] assignments.
          let columnBlocks = b.columnBlocks
          if (!columnBlocks && b.type === 'editorial-grid') {
            const migrated: Partial<Record<'column1' | 'column2' | 'column3', { id: string; type: PageBlockType }[]>> = {}
            for (const k of ['column1', 'column2', 'column3'] as const) {
              const t = b.config[k]
              if (t) migrated[k] = [{ id: crypto.randomUUID(), type: t as PageBlockType }]
            }
            columnBlocks = migrated
          }
          return { id: b.id, type: b.type, order: b.order, config: b.config, columnBlocks }
        }),
    )
  }, [existing]) // eslint-disable-line react-hooks/exhaustive-deps

  const canSave = name.trim() !== '' && usageContext.length > 0 && canvas.slots.length > 0 && !isSaving

  const saveTooltip = isSaving
    ? 'Saving\u2026'
    : name.trim() === '' && usageContext.length === 0 && canvas.slots.length === 0
      ? 'Add a name, usage, and at least one block to save'
      : name.trim() === ''
        ? 'Enter a layout name to save'
        : usageContext.length === 0
          ? 'Select who can use this layout'
          : canvas.slots.length === 0
            ? 'Add at least one block to save'
            : null

  function startEditingName() {
    setDraftName(name)
    setIsEditingName(true)
  }

  function commitName() {
    setIsDirty(true)
    setName(draftName.trim())
    setIsEditingName(false)
  }

  function cancelEditingName() {
    setIsEditingName(false)
  }

  function startEditingDesc() {
    setDraftDesc(description)
    setIsEditingDesc(true)
  }

  function commitDesc() {
    setIsDirty(true)
    setDescription(draftDesc.trim())
    setIsEditingDesc(false)
  }

  function handleSave() {
    if (!canSave) return
    const input = {
      name: name.trim(),
      description: description.trim() || undefined,
      usageContext,
      blocks: canvas.slots.map((slot) => ({
        id: slot.id,
        type: slot.type,
        order: slot.order,
        config: slot.config,
        ...(slot.columnBlocks ? { columnBlocks: slot.columnBlocks } : {}),
      })),
    }
    if (isEditing && id) {
      updateLayout(input, {
        onSuccess: () => { setIsDirty(false); toast.success('Layout updated.') },
        onError: () => toast.error('Failed to save layout. Please try again.'),
      })
    } else {
      createLayout(input, {
        onSuccess: (created) => {
          setIsDirty(false)
          toast.success('Layout saved.')
          navigate(`/admin/layouts/${created.id}`, { replace: true })
        },
        onError: () => toast.error('Failed to save layout. Please try again.'),
      })
    }
  }

  if (isEditing && isLayoutError) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="flex max-w-sm flex-col items-center gap-3 rounded border border-error-line bg-error-fill px-6 py-5 text-center">
          <AlertCircle size={20} className="text-error-fg" aria-hidden="true" />
          <p className="text-sm font-semibold text-error-fg">Failed to load layout</p>
          <p className="text-xs text-error-fg opacity-80">The layout could not be retrieved. It may not exist, or there may be a network issue.</p>
        </div>
      </div>
    )
  }

  return (
    <BlockEditorDndContext canvas={canvas}>
    <div className="flex overflow-hidden" style={{ height: 'calc(100vh - 3rem)' }}>

      {/* Unsaved-changes navigation guard */}
      <Dialog.Root open={blocker.state === 'blocked'} onOpenChange={() => blocker.reset?.()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-6 shadow-xl outline-none"
            aria-describedby="nav-guard-desc"
          >
            <Dialog.Title className="mb-1 text-base font-semibold text-fg">
              Unsaved changes
            </Dialog.Title>
            <Dialog.Description id="nav-guard-desc" className="mb-6 text-sm text-fg-secondary">
              You have unsaved edits. If you leave now, your changes will be lost.
            </Dialog.Description>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => blocker.reset?.()}
                className="rounded border border-line bg-canvas px-4 py-2 text-sm font-semibold text-fg transition-colors hover:bg-fill"
              >
                Stay and keep editing
              </button>
              <button
                type="button"
                onClick={() => blocker.proceed?.()}
                className="rounded border border-red-600 bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
              >
                Discard and leave
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Loading overlay for edit mode */}
      {isEditing && isLoadingLayout && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-canvas/80">
          <Loader2 size={20} className="animate-spin text-fg-secondary" />
        </div>
      )}

      {/* Preview overlay — Radix Dialog for focus trap + Escape key */}
      <Dialog.Root open={showPreview} onOpenChange={setShowPreview}>
        <Dialog.Portal>
          <Dialog.Content
            className="fixed inset-0 z-50 flex flex-col bg-canvas outline-none"
            aria-describedby={undefined}
          >
            <Dialog.Title className="sr-only">Layout Preview</Dialog.Title>
            <LayoutPreview slots={canvas.slots} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <BlockPickerShelf canvas={canvas} hideArticles />

      {/* Center — toolbar + canvas */}
      <section className="flex flex-1 flex-col overflow-hidden">

        {/* Toolbar */}
        <div className="flex items-center gap-3 border-b border-line bg-accent-tertiary-fill px-5 py-2">

          {/* Click-to-edit layout name */}
          <div className="flex shrink-0 items-center gap-2">
            {isEditingName ? (
              <input
                ref={nameInputRef}
                autoFocus
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitName()
                  if (e.key === 'Escape') cancelEditingName()
                }}
                onBlur={commitName}
                placeholder="Layout name…"
                className="w-48 border-b border-line-strong bg-transparent text-sm font-semibold text-fg placeholder:text-fg-secondary focus:border-accent-primary-solid focus:outline-none"
              />
            ) : (
              <button
                type="button"
                onClick={startEditingName}
                className={`border-b border-dashed text-sm font-semibold transition-colors hover:border-line-strong focus:outline-none ${
                  name ? 'border-line text-fg' : 'border-line text-fg-secondary italic'
                }`}
              >
                {name || 'Untitled layout'}
              </button>
            )}
          </div>

          {/* Divider */}
          <span className="text-fg-secondary opacity-20">|</span>

          {/* Click-to-edit description */}
          <div className="flex shrink-0 items-center">
            {isEditingDesc ? (
              <input
                ref={descInputRef}
                autoFocus
                value={draftDesc}
                onChange={(e) => setDraftDesc(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') commitDesc()
                }}
                onBlur={commitDesc}
                placeholder="Add a description…"
                className="w-64 border-b border-line-strong bg-transparent text-xs text-fg placeholder:text-fg-secondary focus:border-accent-primary-solid focus:outline-none"
              />
            ) : (
              <button
                type="button"
                onClick={startEditingDesc}
                className="max-w-64 truncate border-b border-dashed border-line text-xs text-fg-secondary transition-colors hover:border-line-strong hover:text-fg focus:outline-none"
              >
                {description || 'Add description…'}
              </button>
            )}
          </div>

          {/* Divider */}
          <span className="text-fg-secondary opacity-20">|</span>

          {/* Usage context toggles */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">For</span>
            {(['official', 'book'] as LayoutUsageContext[]).map((ctx) => {
              const active = usageContext.includes(ctx)
              return (
                <button
                  key={ctx}
                  type="button"
                  onClick={() => {
                    setUsageContext((prev) =>
                      active ? prev.filter((c) => c !== ctx) : [...prev, ctx],
                    )
                    setIsDirty(true)
                  }}
                  className={`rounded border px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                    active
                      ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
                      : 'border-line bg-canvas text-fg-secondary hover:bg-fill'
                  }`}
                >
                  {ctx === 'official' ? 'Official' : 'Books'}
                </button>
              )
            })}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Actions */}
          <div className="flex shrink-0 items-center gap-2">
            {saveTooltip ? (
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <span className="inline-flex">
                    <button
                      type="button"
                      disabled
                      className="flex cursor-not-allowed items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary opacity-40"
                    >
                      <Save size={12} aria-hidden="true" />
                      Save
                    </button>
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="bottom"
                    sideOffset={6}
                    className="rounded bg-tooltip-bg px-2.5 py-1.5 text-xs text-tooltip-fg shadow-sm animate-in fade-in-0 zoom-in-95"
                  >
                    {saveTooltip}
                    <Tooltip.Arrow className="fill-tooltip-bg" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                className={`flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${isDirty && canSave ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700' : 'border-line bg-canvas text-fg hover:bg-fill'}`}
              >
                {isSaving ? (
                  <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                ) : (
                  <Save size={12} aria-hidden="true" />
                )}
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            )}
            {/* Preview split button */}
            <div className={`flex items-stretch overflow-hidden rounded border border-line bg-canvas text-xs font-semibold text-fg ${canvas.slots.length === 0 ? 'opacity-40 pointer-events-none' : ''}`}>
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                disabled={canvas.slots.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 transition-colors hover:bg-fill"
              >
                <Eye size={12} aria-hidden="true" />
                Preview
              </button>
              <div className="w-px self-stretch bg-line" aria-hidden="true" />
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    disabled={canvas.slots.length === 0}
                    className="flex items-center px-1.5 py-1.5 transition-colors hover:bg-fill"
                    aria-label="More preview options"
                  >
                    <ChevronDown size={11} aria-hidden="true" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    sideOffset={6}
                    align="end"
                    className="z-50 min-w-48 rounded border border-line bg-canvas py-1 shadow-lg"
                  >
                    <DropdownMenu.Item
                      disabled={!id}
                      onSelect={openPopoutPreview}
                      className="flex cursor-default items-center gap-2 px-3 py-1.5 text-xs text-fg outline-none hover:bg-fill focus:bg-fill data-[disabled]:opacity-40 data-[disabled]:pointer-events-none"
                    >
                      <ExternalLink size={12} aria-hidden="true" />
                      Open in new window
                      {!id && <span className="ml-auto text-[10px] text-fg-secondary">Save first</span>}
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
          </div>
        </div>

        <BlockCanvas
          canvas={canvas}
          headerLabel="Layout Structure"
          emptyMessage="Add rows from the left shelf to build a layout."
        />
      </section>

      <LayoutRightShelf canvas={canvas} />

    </div>
    </BlockEditorDndContext>
  )
}

export default LayoutCreator
